import { drawBrandMark } from "@/lib/canvasBrand";
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { publicOrigin } from "@/lib/publicOrigin";
import { Button } from "@/components/ui/button";
import { Download, Share2, Loader2, Copy, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { downloadPoster, sharePoster } from "@/lib/posterShare";

const W = 1080, H = 1080;

const THEMES = [
  { key: "foret", name: "Forêt", nameKey: "poster.theme_foret", swatch: "#1c3a2c", stops: [[0, "#1c3a2c"], [0.55, "#0E1A14"], [1, "#070d0a"]] },
  { key: "or", name: "Doré", nameKey: "poster.theme_or", swatch: "#5a4413", stops: [[0, "#5a4413"], [0.5, "#241a06"], [1, "#0c0900"]] },
  { key: "nuit", name: "Nuit", nameKey: "poster.theme_nuit", swatch: "#1e2a4a", stops: [[0, "#1e2a4a"], [0.55, "#0d1226"], [1, "#05070f"]] },
  { key: "royal", name: "Royal", nameKey: "poster.theme_royal", swatch: "#3a1f52", stops: [[0, "#3a1f52"], [0.55, "#1b0f2b"], [1, "#0a0512"]] },
  { key: "aurore", name: "Aurore", nameKey: "poster.theme_aurore", swatch: "#0f3f45", stops: [[0, "#0f3f45"], [0.55, "#0a2126"], [1, "#04100f"]] },
];

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const wrapText = (ctx, text, x, y, maxWidth, lineHeight, maxLines) => {
  const words = String(text || "").split(" ");
  let line = "";
  const lines = [];
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; }
    else line = test;
  }
  if (line) lines.push(line);
  const shown = lines.slice(0, maxLines);
  shown.forEach((ln, i) => ctx.fillText(ln, x, y + i * lineHeight));
  return shown.length;
};

const coverDraw = (ctx, img) => {
  const ir = img.width / img.height, cr = W / H;
  let dw = W, dh = H, dx = 0, dy = 0;
  if (ir > cr) { dh = H; dw = H * ir; dx = (W - dw) / 2; }
  else { dw = W; dh = W / ir; dy = (H - dh) / 2; }
  ctx.drawImage(img, dx, dy, dw, dh);
};

// Branded invitation poster to grow a church's following on Yalola.
export const ChurchInvitePoster = ({ open, onOpenChange, church }) => {
  const { t } = useApp();
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [building, setBuilding] = useState(true);
  const [themeKey, setThemeKey] = useState(church?.share_accent || "foret");
  const [bgImage, setBgImage] = useState(null);

  // The church's saved accent becomes the poster's default theme.
  useEffect(() => { if (church?.share_accent) setThemeKey(church.share_accent); }, [church?.share_accent]);

  const slug = church ? (church.handle || church.id) : "";
  const shareUrl = church ? `${publicOrigin()}/churches/${slug}` : ""; // clean, human-facing URL shown on the poster
  const shareLink = church ? `${publicOrigin()}/api/e/${slug}` : ""; // server-rendered link that guarantees a rich WhatsApp/Facebook preview
  const location = [church?.city, church?.denomination].filter(Boolean).join(" · ");
  const shareText = church
    ? `⛪ ${church.name}\n${church.city ? `📍 ${church.city}\n` : ""}${t("poster.church_share_text", "Suis cette église sur Yalola pour vivre les directs, annonces et la vie de la communauté 🙏")}`
    : "";

  useEffect(() => {
    if (!open || !church) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) { setBuilding(false); return undefined; }
    setBuilding(true);
    setImgUrl(null);
    let cancelled = false;

    const render = (bgImg) => {
      if (cancelled) return;
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      if (bgImg) {
        coverDraw(ctx, bgImg);
        const ov = ctx.createLinearGradient(0, 0, W, H);
        ov.addColorStop(0, "rgba(8,16,12,0.5)"); ov.addColorStop(1, "rgba(8,16,12,0.88)");
        ctx.fillStyle = ov; ctx.fillRect(0, 0, W, H);
      } else {
        const th = THEMES.find((t) => t.key === themeKey) || THEMES[0];
        const g = ctx.createLinearGradient(0, 0, W, H);
        th.stops.forEach(([o, c]) => g.addColorStop(o, c));
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = "rgba(224,166,60,0.12)";
        ctx.beginPath(); ctx.arc(W * 0.86, H * 0.14, 260, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(W * 0.08, H * 0.92, 220, 0, Math.PI * 2); ctx.fill();
      }

      // Badge
      ctx.fillStyle = "#E0A63C";
      roundRect(ctx, 90, 120, 300, 74, 37); ctx.fill();
      ctx.fillStyle = "#0E1A14"; ctx.font = "bold 40px Outfit, Arial, sans-serif"; ctx.textBaseline = "middle"; ctx.textAlign = "left";
      ctx.fillText(t("poster.church_badge", "⛪ ÉGLISE"), 122, 158);

      // Church name
      ctx.textBaseline = "top";
      ctx.fillStyle = "#fff"; ctx.font = "bold 80px Outfit, Arial, sans-serif";
      const n = wrapText(ctx, church.name, 90, 288, W - 180, 92, 2);
      let cy = 288 + n * 92 + 18;

      if (location) {
        ctx.fillStyle = "#E0A63C"; ctx.font = "600 44px Outfit, Arial, sans-serif";
        ctx.fillText(location, 90, cy);
        cy += 76;
      }

      // Tagline
      ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.font = "40px Inter, Arial, sans-serif";
      wrapText(ctx, "Suis cette église sur Yalola pour ne rien manquer : directs, annonces et vie de la communauté.", 90, cy + 12, W - 180, 54, 3);

      // Call-to-action pill (fixed y, always sits above the brandmark)
      const ctaY = 772;
      ctx.fillStyle = "#D97757";
      roundRect(ctx, 90, ctaY, 640, 92, 46); ctx.fill();
      ctx.fillStyle = "#fff"; ctx.font = "bold 44px Outfit, Arial, sans-serif";
      ctx.textBaseline = "middle";
      ctx.fillText(t("poster.church_cta", "👉  Suis-nous sur Yalola"), 126, ctaY + 48);
      ctx.textBaseline = "top";

      // Brandmark + link
      drawBrandMark(ctx, 150, H - 130, 56);
      ctx.textAlign = "left";
      ctx.fillStyle = "#fff"; ctx.font = "bold 54px Outfit, Arial, sans-serif";
      ctx.fillText("Yalola", 225, H - 148);
      ctx.fillStyle = "rgba(255,255,255,0.7)"; ctx.font = "28px Inter, Arial, sans-serif";
      const shortUrl = shareUrl.replace(/^https?:\/\//, "");
      ctx.fillText(shortUrl, 225, H - 98);

      try {
        canvas.toBlob((b) => { if (!cancelled) { if (b) setImgUrl(URL.createObjectURL(b)); setBuilding(false); } }, "image/png");
      } catch { setBuilding(false); }
    };

    if (bgImage) {
      const im = new Image();
      im.onload = () => render(im);
      im.onerror = () => render(null);
      im.src = bgImage;
    } else {
      render(null);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, church, themeKey, bgImage]);

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (bgImage) URL.revokeObjectURL(bgImage);
    setBgImage(URL.createObjectURL(f));
  };

  const download = () => downloadPoster({ url: imgUrl, fileName: "yalola-eglise.png", t });
  const copyLink = async () => { try { await navigator.clipboard.writeText(`${shareText}\n${shareLink}`); toast.success(t("poster.copied_text", "Texte copié — collez-le sur WhatsApp ou Facebook")); } catch { toast.error(t("poster.copy_fail", "Copie impossible")); } };
  const share = () => sharePoster({ url: imgUrl, text: shareText, link: shareLink, fileName: "yalola-eglise.png", copiedMessage: t("poster.copied_text", "Texte copié — collez-le sur WhatsApp ou Facebook"), t });

  if (!church) return null;

  return (
    <>
      <canvas ref={canvasRef} style={{ display: "none" }} aria-hidden="true" />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm" data-testid="church-invite-modal">
          <DialogHeader>
            <DialogTitle>{t("poster.church_title", "Inviter à suivre l'église")}</DialogTitle>
            <DialogDescription>{t("poster.customize_hint", "Personnalisez l'affiche, puis partagez-la sur WhatsApp, Facebook…")}</DialogDescription>
          </DialogHeader>

          <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)", aspectRatio: "1 / 1" }}>
            {building && <div className="w-full h-full grid place-items-center"><Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} /></div>}
            {!building && imgUrl && <img src={imgUrl} alt={t("poster.church_alt", "Affiche d'invitation")} className="w-full block" data-testid="church-invite-image" />}
          </div>

          <div className="flex items-center gap-2 flex-wrap" data-testid="church-invite-themes">
            {THEMES.map((th) => (
              <button
                key={th.key}
                onClick={() => { setBgImage(null); setThemeKey(th.key); }}
                title={t(th.nameKey, th.name)}
                className="h-8 w-8 rounded-full border-2 transition-transform hover:scale-110"
                style={{ background: th.swatch, borderColor: !bgImage && themeKey === th.key ? "var(--yalola-primary)" : "transparent" }}
                data-testid={`church-invite-theme-${th.key}`}
              />
            ))}
            <label className="h-8 px-2.5 inline-flex items-center gap-1 rounded-full border text-xs font-semibold cursor-pointer" style={{ borderColor: bgImage ? "var(--yalola-primary)" : "var(--yalola-border)", color: "var(--yalola-primary)" }} data-testid="church-invite-image-upload">
              <ImagePlus size={14} /> {t("poster.image", "Image")}
              <input type="file" accept="image/*" className="hidden" onChange={onPickImage} />
            </label>
            {bgImage && (
              <button onClick={() => setBgImage(null)} className="h-8 w-8 grid place-items-center rounded-full text-red-500" title={t("poster.remove_image", "Retirer l'image")} data-testid="church-invite-image-clear"><X size={15} /></button>
            )}
          </div>

          <div className="flex gap-2">
            <Button onClick={share} disabled={building} className="flex-1 pill-primary rounded-full" data-testid="church-invite-share"><Share2 size={16} className="mr-1.5" /> {t("poster.share", "Partager")}</Button>
            <Button onClick={download} disabled={building} variant="outline" className="rounded-full" title={t("poster.download", "Télécharger")} data-testid="church-invite-download"><Download size={16} /></Button>
            <Button onClick={copyLink} variant="outline" className="rounded-full" title={t("poster.copy_link", "Copier le lien")} data-testid="church-invite-copy"><Copy size={16} /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChurchInvitePoster;
